"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";

const CheckIcon = () => (
	<svg
		className="h-5 w-5 flex-shrink-0 text-themePrimary400"
		viewBox="0 0 20 20"
		fill="currentColor"
		aria-hidden="true"
	>
		<path
			fillRule="evenodd"
			d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
			clipRule="evenodd"
		/>
	</svg>
);

interface CategoryConfig {
	tabLabelKey: string;
	titleKey: string;
	descKey: string;
	benefitKeys: string[];
}

const CATEGORIES: CategoryConfig[] = [
	{
		tabLabelKey: "tab1Label",
		titleKey: "cat1Title",
		descKey: "cat1Desc",
		benefitKeys: ["cat1Benefit1", "cat1Benefit2", "cat1Benefit3", "cat1Benefit4", "cat1Benefit5"],
	},
	{
		tabLabelKey: "tab2Label",
		titleKey: "cat2Title",
		descKey: "cat2Desc",
		benefitKeys: ["cat2Benefit1", "cat2Benefit2", "cat2Benefit3", "cat2Benefit4"],
	},
	{
		tabLabelKey: "tab3Label",
		titleKey: "cat3Title",
		descKey: "cat3Desc",
		benefitKeys: ["cat3Benefit1", "cat3Benefit2", "cat3Benefit3", "cat3Benefit4", "cat3Benefit5"],
	},
	{
		tabLabelKey: "tab4Label",
		titleKey: "cat4Title",
		descKey: "cat4Desc",
		benefitKeys: ["cat4Benefit1", "cat4Benefit2", "cat4Benefit3", "cat4Benefit4", "cat4Benefit5"],
	},
	{
		tabLabelKey: "tab5Label",
		titleKey: "cat5Title",
		descKey: "cat5Desc",
		benefitKeys: ["cat5Benefit1", "cat5Benefit2", "cat5Benefit3", "cat5Benefit4", "cat5Benefit5"],
	},
	{
		tabLabelKey: "tab6Label",
		titleKey: "cat6Title",
		descKey: "cat6Desc",
		benefitKeys: ["cat6Benefit1", "cat6Benefit2", "cat6Benefit3", "cat6Benefit4", "cat6Benefit5"],
	},
	{
		tabLabelKey: "tab7Label",
		titleKey: "cat7Title",
		descKey: "cat7Desc",
		benefitKeys: ["cat7Benefit1", "cat7Benefit2", "cat7Benefit3", "cat7Benefit4", "cat7Benefit5"],
	},
	{
		tabLabelKey: "tab8Label",
		titleKey: "cat8Title",
		descKey: "cat8Desc",
		benefitKeys: ["cat8Benefit1", "cat8Benefit2", "cat8Benefit3", "cat8Benefit4", "cat8Benefit5"],
	},
	{
		tabLabelKey: "tab9Label",
		titleKey: "cat9Title",
		descKey: "cat9Desc",
		benefitKeys: ["cat9Benefit1", "cat9Benefit2", "cat9Benefit3", "cat9Benefit4", "cat9Benefit5"],
	},
	{
		tabLabelKey: "tab10Label",
		titleKey: "cat10Title",
		descKey: "cat10Desc",
		benefitKeys: ["cat10Benefit1", "cat10Benefit2", "cat10Benefit3", "cat10Benefit4", "cat10Benefit5"],
	},
	{
		tabLabelKey: "tab11Label",
		titleKey: "cat11Title",
		descKey: "cat11Desc",
		benefitKeys: ["cat11Benefit1", "cat11Benefit2", "cat11Benefit3", "cat11Benefit4", "cat11Benefit5"],
	},
	{
		tabLabelKey: "tab12Label",
		titleKey: "cat12Title",
		descKey: "cat12Desc",
		benefitKeys: ["cat12Benefit1", "cat12Benefit2", "cat12Benefit3", "cat12Benefit4", "cat12Benefit5"],
	},
];

export interface InjectionMoldingSecondaryProcessesProps {
	translationNamespace?: string;
	/** 右侧展示图：不传 images 时，12 个分类共用此图；传了 images（长度 12）则按当前分类切换 */
	imageSrc?: string;
	/** 可选：12 个分类各自图片，与 Tab 一一对应；不传或长度非 12 时用 imageSrc 作为共用图 */
	images?: string[];
}

const DEFAULT_IMAGE = "/image/new-version/molding/injection-molding/services/1.webp";

export default function InjectionMoldingSecondaryProcesses({
	translationNamespace = "injectionMoldingSecondaryProcesses",
	imageSrc = DEFAULT_IMAGE,
	images,
}: InjectionMoldingSecondaryProcessesProps) {
	const t = useTranslations(translationNamespace);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const cat = CATEGORIES[selectedIndex];
	// 有 12 张图则按分类切换，否则用同一张
	const currentImage =
		images && images.length === CATEGORIES.length ? images[selectedIndex] : imageSrc || DEFAULT_IMAGE;

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题 + 简介 */}
				<div className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-gray-900 md:text-base">{t("intro")}</p>
				</div>

				{/* 12 个分类 Tab 按钮 */}
				<div className="mb-8 flex flex-wrap justify-center gap-2 sm:gap-3">
					{CATEGORIES.map((item, index) => (
						<button
							key={item.tabLabelKey}
							type="button"
							onClick={() => setSelectedIndex(index)}
							className={`rounded px-4 py-2.5 text-sm font-medium transition-colors sm:px-5 sm:py-3 sm:text-sm ${
								selectedIndex === index
									? "bg-themePrimary400 text-white"
									: "bg-gray-200 text-black hover:bg-gray-300"
							}`}
						>
							{t(item.tabLabelKey)}
						</button>
					))}
				</div>

				{/* 下方内容区：左文右图 */}
				<div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
					<div className="flex flex-col lg:flex-row">
						{/* 左侧：标题 + Description + Benefits */}
						<div className="flex-1 p-6 md:p-8 lg:p-10">
							<h3 className="mb-6 text-lg font-bold text-black sm:text-xl md:text-2xl">{t(cat.titleKey)}</h3>
							<div className="mb-6">
								<h4 className="mb-2 text-base font-semibold uppercase tracking-wide text-gray-900 sm:text-lg">
									Description
								</h4>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(cat.descKey)}</p>
							</div>
							<div>
								<h4 className="mb-3 text-base font-semibold uppercase tracking-wide text-gray-900 sm:text-lg">
									Benefits
								</h4>
								<ul className="space-y-2">
									{cat.benefitKeys.map((key) => (
										<li
											key={key}
											className="flex items-start gap-2 text-sm leading-relaxed text-black md:text-base"
										>
											<CheckIcon />
											<span>{t(key)}</span>
										</li>
									))}
								</ul>
							</div>
						</div>
						{/* 右侧：图片 */}
						<div className="relative w-full shrink-0 lg:w-[45%]">
							<div className="relative aspect-[4/3] w-full lg:aspect-auto lg:h-full lg:min-h-[320px]">
								<Image
									src={currentImage}
									alt={t(cat.titleKey)}
									fill
									className="object-cover"
									sizes="(max-width: 1024px) 100vw, 45vw"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
